/**
 * ModulesHubPage.tsx — Central hub for advanced placement / optimisation modules
 *
 * Each module is a "tick-of-the-box" enhancement: toggle to enable, the
 * downstream pages start using the new logic; toggle off and they revert to
 * the default behaviour. Coming-soon placeholders show what's next.
 */

import { useNavigate } from 'react-router';
import {
  Boxes, ArrowLeft, CheckCircle2, Clock, ExternalLink,
  Activity, Mountain, Wind, AlertOctagon, PoundSterling,
  Sparkles, GitBranch, Layers, Box, Ban, AlertTriangle,
  Waves, CalendarClock,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { MODULES, useModulesEnabled, type OptModule } from '@/lib/moduleRegistry';
import ModuleInfoModal from '@/components/ModuleInfoModal';

// Icon lookup so registry entries can reference icons by string name
const ICONS: Record<string, typeof Boxes> = {
  Activity, Mountain, Wind, AlertOctagon, PoundSterling,
  Sparkles, GitBranch, Layers, Box,
  Waves, CalendarClock,
};

const CATEGORY_LABEL: Record<OptModule['category'], string> = {
  placement: 'Placement physics',
  optimisation: 'Optimisation algorithms',
  integration: '3rd-party model integration',
};
const CATEGORY_COLOUR: Record<OptModule['category'], string> = {
  placement: 'border-sky-700 bg-sky-900/15',
  optimisation: 'border-violet-700 bg-violet-900/15',
  integration: 'border-emerald-700 bg-emerald-900/15',
};

/* ───────── Topic grouping (finer-grained than category) ───────── */
type Topic =
  | 'plume' | 'bed' | 'oxygen' | 'algae' | 'nutrients'
  | 'mixing' | 'operational' | 'optimisation' | 'integration';

interface TopicMeta {
  label: string;
  short: string;
  desc: string;
  colour: string;
  icon: string;
}
const TOPIC_META: Record<Topic, TopicMeta> = {
  plume:        { label: 'Plume hydraulics & coverage', short: 'Plume',       desc: 'Bubble-plume rise, detrainment, merging, surface coverage and wind interaction.',         colour: 'border-cyan-700 bg-cyan-900/15',       icon: 'Waves' },
  bed:          { label: 'Bed physics & sediment',      short: 'Bed',         desc: 'Sediment oxygen demand, shear, erosion, bioturbation, capping, internal P release.',     colour: 'border-amber-700 bg-amber-900/15',     icon: 'Mountain' },
  oxygen:       { label: 'Hypolimnion oxygen & gas transfer', short: 'Oxygen', desc: 'kLa scaling, gas-transfer coefficients, bubble coalescence, single-bubble dynamics.',  colour: 'border-sky-700 bg-sky-900/15',         icon: 'Droplets' },
  algae:        { label: 'Algae & T&O risk',            short: 'Algae',       desc: 'Cyano-stratification feedbacks, functional-group succession, geosmin / 2-MIB.',          colour: 'border-emerald-700 bg-emerald-900/15', icon: 'FlaskConical' },
  nutrients:    { label: 'Nutrients (N + P)',           short: 'Nutrients',   desc: 'Reckhow/Vollenweider P-retention, N+P co-limitation, redox P release.',                  colour: 'border-lime-700 bg-lime-900/15',       icon: 'Leaf' },
  mixing:       { label: 'Mixing strategy',             short: 'Mixing',      desc: 'PWCM (NL/UK shallow lakes), TEM (Australian deep), Langmuir cells.',                    colour: 'border-violet-700 bg-violet-900/15',   icon: 'Wind' },
  operational:  { label: 'Operational control',          short: 'Operational', desc: 'Seasonal scheduler, regime triggers, nocturnal convection.',                            colour: 'border-rose-700 bg-rose-900/15',       icon: 'CalendarClock' },
  optimisation: { label: 'Optimisation algorithms',     short: 'Optimisation',desc: 'Cost-per-O2 ranking, DDS / NSGA-II auto-optimisers.',                                   colour: 'border-fuchsia-700 bg-fuchsia-900/15', icon: 'Sparkles' },
  integration:  { label: '3rd-party model integration', short: 'Integration', desc: 'GLM (1-D thermal) / ELCOM-CAEDYM (3-D thermal + ecology) couplings.',                    colour: 'border-slate-600 bg-slate-800/30',     icon: 'GitBranch' },
};
const TOPIC_ORDER: Topic[] = ['plume', 'bed', 'oxygen', 'algae', 'nutrients', 'mixing', 'operational', 'optimisation', 'integration'];

/** Map every module id to a topic. */
function topicOf(id: string): Topic {
  // Mixing strategy
  if (['pwcm_visser', 'tem_epilimnetic', 'langmuir'].includes(id)) return 'mixing';
  // Operational
  if (['seasonal_scheduler', 'macintyre_convection', 'wind_tilt', 'internal_seiche'].includes(id)) return 'operational';
  // Optimisation
  if (['cost_per_o2', 'dds_optimiser', 'nsga2'].includes(id)) return 'optimisation';
  // Integration
  if (['glm_coupling', 'elcom_coupling'].includes(id)) return 'integration';
  // Algae
  if (['kumagai_feedback', 'csr_groups', 'geosmin_predictor'].includes(id)) return 'algae';
  // Nutrients
  if (['reckhow_p_retention', 'np_colimitation', 'redox_p_release', 'davison_fe_mn'].includes(id)) return 'nutrients';
  // Bed
  if (['boudreau_diagenesis', 'aller_bioturbation', 'mehta_partheniades', 'sediment_capping', 'nom_sod', 'resusp_penalty'].includes(id)) return 'bed';
  // Oxygen / gas transfer
  if (['higbie_kl', 'coalescence', 'mcginnis_bubble'].includes(id)) return 'oxygen';
  // Plume (default for remaining placement modules)
  return 'plume';
}

export default function ModulesHubPage() {
  const navigate = useNavigate();
  const { enabled, toggle, enabledCount, blockInfo, softWarnings } = useModulesEnabled();

  // Group by topic (finer-grained) rather than the original 3-bucket category
  const groupedByTopic = TOPIC_ORDER.map(topic => ({
    topic,
    meta: TOPIC_META[topic],
    items: MODULES.filter(m => topicOf(m.id) === topic),
  })).filter(g => g.items.length > 0);

  const availableCount = MODULES.filter(m => m.status === 'available').length;
  const comingCount = MODULES.filter(m => m.status === 'coming_soon').length;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Boxes}
        title="Advanced Modules — Tick to Enable"
        subtitle="Optional placement and optimisation physics. Toggle on to apply across all relevant pages."
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border-2 border-rb-brand/40 bg-rb-brand/10 p-4">
          <div className="text-xs uppercase tracking-wider text-rb-brand">Modules enabled</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">{enabledCount}</div>
          <div className="text-xs text-slate-400 mt-1">of {availableCount} available</div>
        </div>
        <div className="rounded-xl border border-emerald-700 bg-emerald-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Available now</div>
          <div className="text-3xl font-bold text-emerald-200 mt-1">{availableCount}</div>
        </div>
        <div className="rounded-xl border border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Coming soon</div>
          <div className="text-3xl font-bold text-amber-200 mt-1">{comingCount}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Categories</div>
          <div className="text-3xl font-bold text-slate-100 mt-1">{Object.keys(CATEGORY_LABEL).length}</div>
        </div>
      </div>

      {/* Topic-grouped table of contents - quick navigation to any sub-section */}
      <section className="rounded-xl border border-slate-700 bg-slate-900/40 p-4">
        <div className="text-sm font-bold text-slate-100 mb-2">Browse by topic</div>
        <p className="text-xs text-slate-400 mb-3">
          {MODULES.length} modules grouped into {groupedByTopic.length} topic areas. Click any chip to jump to that
          section. All individual module pages are reachable from the cards below (no more sidebar clutter).
        </p>
        <div className="flex flex-wrap gap-2">
          {groupedByTopic.map(g => (
            <a key={g.topic} href={`#topic-${g.topic}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-slate-700 bg-slate-900/60 text-slate-200 hover:border-cyan-500 hover:text-cyan-200 transition-colors">
              <span className="font-bold">{g.meta.short}</span>
              <span className="text-[10px] text-slate-500">({g.items.length})</span>
            </a>
          ))}
        </div>
      </section>

      {groupedByTopic.map(group => (
        <section id={`topic-${group.topic}`} key={group.topic} className={`rounded-xl border-2 ${group.meta.colour} p-4 scroll-mt-4`}>
          <div className="mb-3">
            <div className="text-base font-bold text-slate-100">{group.meta.label}</div>
            <div className="text-[11px] text-slate-300 mt-0.5">{group.meta.desc}</div>
            <div className="text-[10px] text-slate-500 mt-1">{group.items.length} modules</div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {group.items.map(m => {
              const Icon = ICONS[m.iconName] ?? Boxes;
              const isOn = Boolean(enabled[m.id]);
              const comingSoon = m.status === 'coming_soon';
              const block = !isOn && !comingSoon ? blockInfo(m.id) : null;
              const warnings = !comingSoon && isOn ? softWarnings(m.id) : [];
              const blocked = block !== null;
              const accent = comingSoon ? '#94a3b8' : blocked ? '#f87171' : isOn ? '#34d399' : 'var(--rb-brand)';
              return (
                <div
                  key={m.id}
                  className={`rb-feature-card ${comingSoon ? 'rb-feature-card--disabled' : ''}`}
                  style={{ ['--rb-fc-accent' as string]: accent }}
                >
                  <span className="rb-feature-card__line" aria-hidden
                    style={{ background: `linear-gradient(to right, transparent, ${accent}66, transparent)` }} />
                  <div className="flex items-start gap-3">
                    <div
                      className="rb-feature-card__icon"
                      style={{ background: `${accent}15`, borderColor: `${accent}30`, color: accent }}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <div className="text-sm font-bold text-slate-100">{m.label}</div>
                        {comingSoon ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 uppercase tracking-wider">
                            <Clock className="w-3 h-3" /> Coming soon
                          </span>
                        ) : blocked ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-900/40 text-red-300 uppercase tracking-wider">
                            <Ban className="w-3 h-3" /> Blocked
                          </span>
                        ) : isOn ? (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 uppercase tracking-wider">
                            <CheckCircle2 className="w-3 h-3" /> Enabled
                          </span>
                        ) : null}
                        {warnings.length > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 uppercase tracking-wider">
                            <AlertTriangle className="w-3 h-3" /> {warnings.length} warn
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-300 mt-1">{m.shortDesc}</div>
                      <div className="text-[10px] text-slate-500 italic mt-1">{m.ref}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {m.affects.map((a, i) => (
                          <span key={i} className="inline-block text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">
                            affects: {a}
                          </span>
                        ))}
                      </div>

                      {/* Hard-conflict block message */}
                      {blocked && block && (
                        <div className="mt-2 rounded-md border border-red-700/40 bg-red-900/15 p-2 text-[11px] text-red-200">
                          <div className="flex items-center gap-1.5 font-semibold mb-1">
                            <Ban className="w-3.5 h-3.5" /> Blocked by "{block.blockedBy}"
                          </div>
                          <div className="text-red-100/80 leading-relaxed">{block.reason}</div>
                        </div>
                      )}

                      {/* Soft warnings */}
                      {warnings.map((w, i) => (
                        <div key={i} className="mt-2 rounded-md border border-amber-700/40 bg-amber-900/15 p-2 text-[11px] text-amber-200">
                          <div className="flex items-center gap-1.5 font-semibold mb-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> Soft conflict with "{w.withLabel}"
                          </div>
                          <div className="text-amber-100/80 leading-relaxed">{w.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    {!comingSoon ? (
                      <label className={`flex items-center gap-2 select-none ${blocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input type="checkbox" checked={isOn}
                          disabled={blocked}
                          onChange={() => toggle(m.id)}
                          className="w-4 h-4 accent-emerald-500" />
                        <span className="text-xs text-slate-200">
                          {blocked ? 'Cannot enable — hard conflict above' :
                           isOn ? 'Enabled — active across all relevant pages' : 'Tick to enable'}
                        </span>
                      </label>
                    ) : (
                      <span className="text-xs text-slate-500 italic">Implementation in progress</span>
                    )}
                    <div className="flex items-center gap-2">
                      <ModuleInfoModal moduleId={m.id} triggerLabel="Info" />
                      {m.path && !comingSoon && (
                        <button onClick={() => navigate(m.path!)}
                          className="inline-flex items-center gap-1 text-xs text-sky-300 hover:text-sky-200">
                          Open <ExternalLink className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">How tick-of-the-box works:</strong> Each enabled module is active
        across the pages listed under "affects". For example, ticking Asaeda-Imberger adds a new row at L7
        of the Validation Steps page checking the merging C-criterion. Ticking the Resuspension Penalty
        flag adds a row at L6 flagging modules placed over organic-rich substrate. The Reservoir Hub shows
        a live count of enabled modules.
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4 text-xs text-slate-400 leading-relaxed">
        <strong className="text-slate-200">Topic categorisation:</strong> the 9 topic groups above are
        cross-cutting views of the same {MODULES.length} modules. A module's primary topic is its dominant
        physical role: Bed = bed shear / SOD / sediment dynamics; Oxygen = gas-transfer / bubble physics;
        Algae = cyano feedback / T&amp;O; Mixing = strategy-level regime choice (PWCM / TEM / Langmuir);
        Operational = scheduling and natural-mixing control loops; Optimisation = solver algorithms;
        Integration = couplings to third-party 1-D/3-D models (GLM / ELCOM-CAEDYM). A module's underlying
        category (placement / optimisation / integration) is still queryable via the registry.
      </div>
    </div>
  );
}
